<script>
  import { onMount, tick } from 'svelte'
  import {
    login,
    checkAuth,
    verifyOtp,
    resendOtp,
    otpStatus,
    landingUrl,
  } from '../api.js'
  import { spaPath, absoluteNextUrl, goAfterAuth } from '../lib/historyNav.js'
  import { defaultPathForSite } from '../lib/hostSite.js'
  import { authUser } from '../stores.js'
  import OtpChallenge from '../components/OtpChallenge.svelte'
  import ThemeToggle from '../components/ThemeToggle.svelte'

  // Apex moon landing — cross-subdomain from product hosts /login
  const siteHref = landingUrl()
  const homePath = defaultPathForSite()

  let username = ''
  let password = ''
  let error = ''
  let busy = false
  let step = 'password' // password | otp
  let otpTtl = 300
  let otpError = ''
  let otpBusy = false
  let resendIn = 0
  let resendTimer
  let otpRef

  function nextFromQuery() {
    const q = new URLSearchParams(window.location.search)
    const raw = q.get('next') || homePath
    // Keep absolute paper/live/dashboard next for hard navigation after auth.
    if (absoluteNextUrl(raw)) return raw
    return spaPath(raw, homePath)
  }

  function startResendCooldown(sec = 45) {
    const n = Math.max(0, Math.floor(Number(sec) || 0))
    resendIn = n
    if (resendTimer) clearInterval(resendTimer)
    resendTimer = null
    if (n <= 0) return
    resendTimer = setInterval(() => {
      resendIn = Math.max(0, resendIn - 1)
      if (resendIn <= 0 && resendTimer) {
        clearInterval(resendTimer)
        resendTimer = null
      }
    }, 1000)
  }

  function applyResendInFrom(data, fallback = 45) {
    const raw = data && data.resend_in
    if (raw != null && Number.isFinite(Number(raw))) {
      startResendCooldown(Number(raw))
      return
    }
    startResendCooldown(fallback)
  }

  function isResendCooldownError(err) {
    return err === 'otp_resend_cooldown' || err === 'otp_cooldown'
  }

  function retryAfterSec(r) {
    const fromBody = r && r.data && r.data.retry_after
    if (fromBody != null && Number.isFinite(Number(fromBody))) {
      return Math.max(0, Math.floor(Number(fromBody)))
    }
    const hdr =
      r &&
      r.res &&
      typeof r.res.headers?.get === 'function' &&
      r.res.headers.get('Retry-After')
    if (hdr != null && Number.isFinite(Number(hdr))) {
      return Math.max(0, Math.floor(Number(hdr)))
    }
    return 0
  }

  function formatWait(sec) {
    const n = Math.max(0, Math.floor(Number(sec) || 0))
    if (n <= 0) return ''
    if (n < 60) return `${n} сек`
    const m = Math.floor(n / 60)
    const s = n % 60
    if (s === 0) return `${m} мин`
    return `${m} мин ${s} сек`
  }

  function lockOrWaitMessage(err, sec, { lockedFallback, waitFallback }) {
    const wait = formatWait(sec)
    if (
      err === 'login_locked' ||
      err === 'otp_locked' ||
      err === 'too_many_attempts'
    ) {
      return wait
        ? `Слишком много попыток — подождите ${wait}`
        : lockedFallback
    }
    if (err === 'login_backoff' || err === 'otp_backoff') {
      return wait ? `Подождите ${wait} перед следующей попыткой` : waitFallback
    }
    return null
  }

  onMount(async () => {
    const a = await checkAuth()
    if (a.ok) {
      authUser.set(a.user)
      await goAfterAuth(nextFromQuery(), homePath)
      return
    }
    const q = new URLSearchParams(window.location.search)
    if (q.get('otp') === '1') {
      const st = await otpStatus()
      if (st.ok && st.data && st.data.pending) {
        step = 'otp'
        otpTtl = st.data.expires_in || st.data.otp_ttl_seconds || 300
        applyResendInFrom(st.data, 45)
      }
    }
  })

  async function onSubmit(e) {
    e.preventDefault()
    if (busy) return
    busy = true
    error = ''
    try {
      const next = nextFromQuery()
      const r = await login(username.trim(), password, next)
      if (!r.ok) {
        const err = (r.data && r.data.error) || ''
        const wait = retryAfterSec(r)
        error =
          lockOrWaitMessage(err, wait, {
            lockedFallback: 'Слишком много попыток — вход временно заблокирован',
            waitFallback: 'Подождите перед следующей попыткой',
          }) ||
          (err === 'telegram_send_failed' && 'Не удалось отправить код в Telegram') ||
          (err === 'telegram_not_configured' && '2FA не настроена на сервере') ||
          (wait > 0 && err === 'invalid_credentials'
            ? `Неверный логин или пароль — подождите ${formatWait(wait)}`
            : null) ||
          'Неверный логин или пароль'
        return
      }
      if (r.data && r.data.requires_2fa) {
        step = 'otp'
        otpTtl = r.data.otp_ttl_seconds || 300
        otpError = ''
        applyResendInFrom(r.data, 45)
        await tick()
        otpRef && otpRef.reset({ ttl: otpTtl })
        return
      }
      authUser.set(username.trim())
      await goAfterAuth((r.data && r.data.redirect) || next, homePath)
    } catch {
      error = 'Сеть недоступна'
    } finally {
      busy = false
    }
  }

  async function onOtpSubmit(e) {
    const code = e.detail.code
    if (otpBusy) return
    otpBusy = true
    otpError = ''
    try {
      const next = nextFromQuery()
      const r = await verifyOtp(code, next)
      if (!r.ok) {
        const err = (r.data && r.data.error) || ''
        const wait = retryAfterSec(r)
        const lockMsg = lockOrWaitMessage(err, wait, {
          lockedFallback: 'Код заблокирован — войдите снова',
          waitFallback: 'Подождите перед следующей попыткой',
        })
        otpError =
          lockMsg ||
          (err === 'otp_expired' && 'Код истёк — войдите снова') ||
          (err === 'expired' && 'Код истёк — войдите снова') ||
          (err === 'otp_required' && 'Сначала войдите логином') ||
          (wait > 0 && err === 'invalid_code'
            ? `Неверный код — подождите ${formatWait(wait)}`
            : null) ||
          'Неверный код'
        if (
          err === 'otp_locked' ||
          err === 'otp_expired' ||
          err === 'expired' ||
          err === 'otp_required' ||
          err === 'too_many_attempts' ||
          err === 'login_locked'
        ) {
          step = 'password'
          password = ''
        } else if (otpRef) {
          await otpRef.failShake()
        }
        return
      }
      if (otpRef) otpRef.pulseSuccess()
      authUser.set(username.trim() || 'ok')
      await new Promise((r) => setTimeout(r, 420))
      await goAfterAuth((r.data && r.data.redirect) || next, homePath)
    } catch {
      otpError = 'Сеть недоступна'
      if (otpRef) await otpRef.failShake()
    } finally {
      otpBusy = false
    }
  }

  async function onOtpResend() {
    if (otpBusy || resendIn > 0) return
    otpBusy = true
    otpError = ''
    try {
      const r = await resendOtp()
      if (!r.ok) {
        const err = (r.data && r.data.error) || ''
        const wait = retryAfterSec(r)
        if (isResendCooldownError(err)) {
          applyResendInFrom(r.data, 45)
          otpError = 'Подождите перед повторной отправкой'
          return
        }
        otpError =
          lockOrWaitMessage(err, wait, {
            lockedFallback: 'Код заблокирован — войдите снова',
            waitFallback: 'Подождите перед следующей попыткой',
          }) ||
          (err === 'otp_expired' && 'Код истёк — войдите снова') ||
          (err === 'otp_required' && 'Сначала войдите логином') ||
          (err === 'too_many_attempts' && 'Слишком много попыток — войдите снова') ||
          (err === 'telegram_send_failed' && 'Telegram не принял сообщение') ||
          'Не удалось отправить код'
        if (
          err === 'otp_expired' ||
          err === 'otp_required' ||
          err === 'too_many_attempts' ||
          err === 'otp_locked'
        ) {
          step = 'password'
          password = ''
        }
        return
      }
      otpTtl = (r.data && r.data.otp_ttl_seconds) || otpTtl
      applyResendInFrom(r.data, 45)
      otpError = ''
      if (otpRef) otpRef.reset({ ttl: otpTtl })
    } catch {
      otpError = 'Сеть недоступна'
    } finally {
      otpBusy = false
    }
  }

  function backToPassword() {
    step = 'password'
    otpError = ''
    password = ''
  }
</script>

<div class="pm-atmosphere" aria-hidden="true">
  <div class="pm-atmosphere__grain"></div>
  {#if step === 'otp'}
    <div class="pm-login-breathe"></div>
  {/if}
</div>

<div class="pm-topbar">
  <ThemeToggle variant="header" />
</div>

<div class="pm-login-wrap">
  <div class="pm-login-stack" class:pm-login-stack--otp={step === 'otp'}>
    {#if step === 'password'}
      <form class="pm-login-card pm-surface" on:submit={onSubmit}>
        <a class="pm-eyebrow pm-login-brand" href={siteHref}>lunes crypto</a>
        <h1 class="pm-title">Вход</h1>
        <p class="pm-meta">Пароль · затем код в Telegram</p>

        <label for="user">Логин</label>
        <input
          id="user"
          name="username"
          autocomplete="username"
          bind:value={username}
          required
          disabled={busy}
        />

        <label for="pass">Пароль</label>
        <input
          id="pass"
          name="password"
          type="password"
          autocomplete="current-password"
          bind:value={password}
          required
          disabled={busy}
        />

        {#if error}
          <p class="pm-error" style="margin-top:0.85rem">{error}</p>
        {/if}

        <div class="pm-login-actions">
          <button type="submit" class="pm-btn pm-btn--accent" disabled={busy}>
            {busy ? 'Вход…' : 'Продолжить'}
          </button>
        </div>
      </form>
    {:else}
      <OtpChallenge
        bind:this={otpRef}
        ttlSeconds={otpTtl}
        busy={otpBusy}
        error={otpError}
        {resendIn}
        on:submit={onOtpSubmit}
        on:resend={onOtpResend}
      />
    {/if}

    <nav class="pm-login-foot" aria-label="Дополнительно">
      {#if step === 'otp'}
        <button type="button" class="pm-otp-back" on:click={backToPassword} disabled={otpBusy}>
          ← Другой аккаунт
        </button>
      {/if}
      <a class="pm-login-site" href={siteHref}>← На сайт</a>
    </nav>
  </div>
</div>
